import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "../components/Logo";

const features = [
  { icon: "🏍️", title: "Sales Bot", desc: "Product details, pricing, test booking — fully automated responses for every customer query, 24/7." },
  { icon: "🔧", title: "Service Bot", desc: "Service scheduling, staff alerts, and customer query resolution without lifting a finger." },
  { icon: "🤖", title: "AI Assistant", desc: "Powered by Claude AI — natural conversations, smart replies, and context-aware responses in any language." },
  { icon: "📊", title: "Lead Capture", desc: "Every customer's details automatically saved to your CRM and Google Sheet in real time." },
  { icon: "🔔", title: "Instant Alerts", desc: "Hot lead notifications via Email, WhatsApp, and Telegram the moment they arrive." },
  { icon: "📱", title: "24/7 Active", desc: "Never miss a message — your bot replies round the clock, even while you sleep." },
];

const forList = [
  { icon: "🏍️", title: "Vehicle Dealerships", desc: "Hero, Honda, TVS, Bajaj, Toyota, Hyundai — automate sales and service queries effortlessly worldwide." },
  { icon: "🏨", title: "Hotels & Resorts", desc: "Room inquiries, booking confirmations, and guest service — all on WhatsApp, globally." },
  { icon: "🛒", title: "Retail & E-commerce", desc: "Product queries, order updates, and customer support — automated for any retail business." },
  { icon: "🏢", title: "Any Business", desc: "Real estate, restaurants, clinics, agencies — any business on WhatsApp can benefit from BotSaathi." },
];

const plans = [
  {
    name: "Starter", setup: "$49", setupINR: "₹3,999", monthly: "$24/month", monthlyINR: "₹1,999/mo", color: "#4d9fff", popular: false,
    features: ["1 WhatsApp Bot", "Lead Capture & CRM", "Google Sheet Sync", "Email + Telegram Alerts", "1 Month Support"],
  },
  {
    name: "Growth", setup: "$99", setupINR: "₹7,999", monthly: "$29/month", monthlyINR: "₹2,499/mo", color: "#25d366", popular: true,
    features: ["Everything in Starter", "Custom Bot Flow", "Product Catalog", "All Notification Channels", "3 Month Support"],
  },
  {
    name: "Scale", setup: "Custom", setupINR: "Custom", monthly: "Custom", monthlyINR: "Custom", color: "#ff9500", popular: false,
    features: ["Everything in Growth", "Multiple Bots", "AI Calling Engine", "Priority Support", "Custom Integration"],
  },
];

const comingSoon = [
  { icon: "📞", title: "Auto Call on Hot Lead", desc: "Bot detects a hot lead and calls them within seconds — zero human intervention." },
  { icon: "🎙️", title: "Voice Transcription", desc: "Every call automatically transcribed, summarized, and saved to your dashboard." },
  { icon: "🤖", title: "AI Voice Conversation", desc: "Natural AI voice handles the entire sales call — books appointments, answers queries." },
  { icon: "📊", title: "Call Analytics", desc: "Track call duration, outcomes, sentiment, and conversion rates in real time." },
];

const BUSINESS_TYPES = ["Vehicle Dealership", "Hotel / Resort", "Real Estate", "Retail Shop", "Restaurant", "E-commerce", "Clinic / Healthcare", "Other"];
const WA_LINK = "https://wa.me/918650373635";

const testimonials = [
  { name: "Hero Motors, Ramnagar", text: "Bot handles 50+ customer queries daily. No missed leads after hours.", flag: "🇮🇳" },
  { name: "WhatsApp Automation User", text: "Setup was done in 24 hours. Leads started coming in the same day.", flag: "🌍" },
  { name: "Local Business Owner", text: "Customers get instant replies even at midnight. Game changer.", flag: "🌏" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", business: "", type: "" });
  const [formState, setFormState] = useState("idle");

  const handleDemo = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setFormState("sending");
    try {
      const msg = `🚀 *New Demo Request — BotSaathi*\n\n👤 Name: ${form.name}\n📞 Phone/WhatsApp: ${form.phone}\n🏢 Business: ${form.business || "—"}\n📂 Type: ${form.type || "—"}`;
      const waUrl = `https://wa.me/918650373635?text=${encodeURIComponent(msg)}`;
      window.open(waUrl, "_blank");
      setFormState("done");
    } catch {
      setFormState("error");
    }
  };

  return (
    <div style={s.wrapper}>
      <Helmet>
        <title>BotSaathi — AI WhatsApp Chatbot for Business | Auto-Reply, Lead Capture, CRM</title>
        <meta name="description" content="BotSaathi automates your WhatsApp with AI — 24/7 customer replies, lead capture, service booking, and instant alerts. Works for dealerships, hotels, retail, and any business worldwide. No coding needed." />
        <meta name="keywords" content="WhatsApp chatbot for business, AI WhatsApp bot, WhatsApp automation, WhatsApp lead capture, WhatsApp CRM, WhatsApp bot for dealership, WhatsApp bot for hotel, custom WhatsApp chatbot, WhatsApp business automation, auto reply WhatsApp, WhatsApp bot developer, AI chatbot WhatsApp, WhatsApp API integration, WhatsApp bot UAE, WhatsApp bot Saudi Arabia, WhatsApp automation India, WhatsApp bot for small business" />
        <meta property="og:title" content="BotSaathi — AI WhatsApp Chatbot for Any Business Worldwide" />
        <meta property="og:description" content="24/7 AI WhatsApp bot — auto-reply, lead capture, CRM, instant alerts. Works for dealerships, hotels, retail and any business. Setup in 24 hours." />
        <meta property="og:url" content="https://botsaathi.com/" />
        <link rel="canonical" href="https://botsaathi.com/" />
      </Helmet>
      <style>{`
        @keyframes glow { 0%,100%{opacity:0.4} 50%{opacity:1} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .fc:hover { border-color: rgba(37,211,102,0.4) !important; transform: translateY(-2px); transition: all 0.2s; }
        .nl:hover { color: #25d366 !important; }
        .pc:hover { transform: translateY(-4px); transition: all 0.25s; }
        .demo-input { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 14px 18px; color: #f0f0f0; font-size: 14px; outline: none; width: 100%; box-sizing: border-box; transition: border-color 0.2s; font-family: inherit; }
        .demo-input:focus { border-color: rgba(37,211,102,0.4); }
        .demo-input::placeholder { color: #444; }
        .demo-select { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 14px 18px; color: #f0f0f0; font-size: 14px; outline: none; width: 100%; box-sizing: border-box; cursor: pointer; font-family: inherit; appearance: none; }
        .demo-select option { background: #111; color: #f0f0f0; }
        .tc:hover { border-color: rgba(37,211,102,0.3) !important; transition: all 0.2s; }
      `}</style>

      <div style={s.gridBg} />
      <div style={s.orb1} />
      <div style={s.orb2} />

      {/* Header */}
      <header style={s.header}>
        <Logo size={30} showText={true} />
        <nav style={s.headerNav}>
          {[["#features","Features"],["#demo","Get Demo"],["#pricing","Pricing"],["/widget","Chat Widget"]].map(([h,l]) => (

            <a key={h} href={h} className="nl" style={s.navLink}>{l}</a>
          ))}
          <a href={WA_LINK} target="_blank" rel="noreferrer" className="nl" style={s.navLink}>Contact</a>
        </nav>
        <button onClick={() => navigate("/login")} style={s.loginBtn}>Login →</button>
      </header>

      {/* Hero */}
      <section style={s.hero}>
        <div style={s.heroBadge}>
          <span style={s.badgeDot} />
          AI-Powered WhatsApp Automation · Works Worldwide
        </div>
        <h1 style={s.heroTitle}>
          Turn WhatsApp into Your<br />
          <span style={{ color: "#25d366" }}>24/7 Sales Machine</span>
        </h1>
        <p style={s.heroSubtitle}>
          Auto-reply to customers, capture leads, book appointments, and get instant alerts —
          all from your WhatsApp number. No coding. No missed messages. Ever.
        </p>
        <div style={s.heroBtns}>
          <a href="#demo" style={s.primaryBtn}>🚀 Get Free Demo</a>
          <a href={WA_LINK} target="_blank" rel="noreferrer" style={s.secondaryBtn}>📱 WhatsApp Us</a>
        </div>
        <div style={s.statsRow}>
          {[["24/7","Always Active"],["< 2s","Response Time"],["100%","Automated"],["∞","Conversations"]].map(([v,l],i) => (
            <div key={i} style={s.statItem}>
              <div style={s.statVal}>{v}</div>
              <div style={s.statLabel}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section style={s.section} id="features">
        <div style={s.tag}>CAPABILITIES</div>
        <h2 style={s.title}>Everything Your Business Needs</h2>
        <p style={s.subtitle}>One bot. Every customer interaction. Zero missed leads.</p>
        <div style={s.featuresGrid}>
          {features.map((f,i) => (
            <div key={i} className="fc" style={s.featureCard}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>{f.icon}</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{f.title}</div>
              <div style={{ fontSize: 14, color: "#555", lineHeight: 1.7 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Who */}
      <section style={s.altSection}>
        <div style={s.inner}>
          <div style={s.tag}>USE CASES</div>
          <h2 style={s.title}>Works for Any Business, Anywhere</h2>
          <p style={s.subtitle}>If your customers message you on WhatsApp — BotSaathi can automate it.</p>
          <div style={s.forGrid}>
            {forList.map((f,i) => (
              <div key={i} style={s.forCard}>
                <div style={{ fontSize: 40, marginBottom: 14 }}>{f.icon}</div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{f.title}</div>
                <div style={{ fontSize: 14, color: "#555", lineHeight: 1.7 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={s.section}>
        <div style={s.tag}>TESTIMONIALS</div>
        <h2 style={s.title}>What Our Clients Say</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginTop: 40 }}>
          {testimonials.map((t, i) => (
            <div key={i} className="tc" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 14, padding: 24 }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>⭐⭐⭐⭐⭐</div>
              <div style={{ fontSize: 14, color: "#888", lineHeight: 1.8, marginBottom: 16, fontStyle: "italic" }}>"{t.text}"</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>{t.flag} {t.name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Demo Form */}
      <section style={s.altSection} id="demo">
        <div style={s.inner}>
          <div style={s.tag}>GET STARTED</div>
          <h2 style={s.title}>Request a Free Demo</h2>
          <p style={s.subtitle}>Fill in your details — we'll set up a live demo for your business within 24 hours.</p>

          <div style={s.demoWrapper}>
            <div style={s.demoInfo}>
              <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "Georgia, serif", marginBottom: 16, lineHeight: 1.3 }}>
                See BotSaathi live on your WhatsApp
              </div>
              <div style={{ fontSize: 14, color: "#555", lineHeight: 1.9, marginBottom: 28 }}>
                We personally set up the demo for your business. You'll see exactly how it works — customer queries, lead capture, instant alerts — all automated.
              </div>
              {[
                ["⚡", "Setup in 24 hours"],
                ["🎯", "Customized for your business"],
                ["🌍", "Works globally — any country"],
                ["🔒", "No commitment required"],
              ].map(([icon, text], i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <span style={{ fontSize: 20 }}>{icon}</span>
                  <span style={{ fontSize: 14, color: "#888" }}>{text}</span>
                </div>
              ))}
            </div>

            <div style={s.demoForm}>
              {formState === "done" ? (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Request Sent!</div>
                  <div style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>We'll contact you within 24 hours to set up your demo.</div>
                  <button onClick={() => setFormState("idle")} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#888", padding: "10px 20px", fontSize: 13, cursor: "pointer" }}>
                    Submit Another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleDemo} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={s.label}>Your Name *</label>
                    <input className="demo-input" placeholder="John Smith / Rajesh Kumar" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                  </div>
                  <div>
                    <label style={s.label}>WhatsApp Number *</label>
                    <input className="demo-input" placeholder="+1 555 000 0000 / +91 98765 43210" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} required />
                  </div>
                  <div>
                    <label style={s.label}>Business Name</label>
                    <input className="demo-input" placeholder="Your Business Name" value={form.business} onChange={e => setForm(p => ({ ...p, business: e.target.value }))} />
                  </div>
                  <div>
                    <label style={s.label}>Business Type</label>
                    <select className="demo-select" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                      <option value="">Select type...</option>
                      {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={formState === "sending"}
                    style={{ background: "#25d366", color: "#000", border: "none", borderRadius: 10, padding: "15px", fontSize: 15, fontWeight: 700, cursor: formState === "sending" ? "not-allowed" : "pointer", marginTop: 4, opacity: formState === "sending" ? 0.7 : 1, boxShadow: "0 0 24px rgba(37,211,102,0.2)" }}
                  >
                    {formState === "sending" ? "Sending..." : "🚀 Request Free Demo"}
                  </button>
                  <div style={{ fontSize: 11, color: "#444", textAlign: "center" }}>
                    We respond within 24 hours · No spam · Works worldwide
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={s.section} id="pricing">
        <div style={s.tag}>PRICING</div>
        <h2 style={s.title}>Simple, Transparent Pricing</h2>
        <p style={s.subtitle}>No hidden charges. Prices in USD and INR. Pay only for what you use.</p>
        <div style={s.pricingGrid}>
          {plans.map((plan,i) => (
            <div key={i} className="pc" style={{ ...s.pricingCard, border: plan.popular ? "2px solid "+plan.color : "1px solid rgba(255,255,255,0.06)", position: "relative" }}>
              {plan.popular && (
                <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: plan.color, padding: "4px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700, color: "#000", whiteSpace: "nowrap" }}>
                  ⭐ Most Popular
                </div>
              )}
              <div style={{ fontSize: 18, fontWeight: 700, color: plan.color }}>{plan.name}</div>
              <div style={{ fontSize: 28, fontWeight: 700, marginTop: 8 }}>{plan.setup} <span style={{ fontSize: 14, color: "#444", fontWeight: 400 }}>setup</span></div>
              <div style={{ fontSize: 12, color: "#444", marginTop: -4 }}>{plan.setupINR} setup</div>
              <div style={{ fontSize: 15, color: "#666", marginTop: 4 }}>{plan.monthly}</div>
              <div style={{ fontSize: 11, color: "#444" }}>{plan.monthlyINR}</div>
              <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "12px 0" }} />
              {plan.features.map((f,j) => <div key={j} style={{ fontSize: 13, color: "#666", padding: "3px 0" }}>✓ {f}</div>)}
              <a href="#demo" style={{ display: "block", textAlign: "center", textDecoration: "none", color: "#000", background: plan.color, padding: "12px", borderRadius: 8, fontWeight: 700, fontSize: 14, marginTop: 16 }}>
                Get Started
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Coming Soon */}
      <section style={s.altSection} id="roadmap">
        <div style={s.inner}>
          <div style={s.tag}>COMING SOON</div>
          <h2 style={s.title}>Next-Gen AI Voice Calling</h2>
          <p style={s.subtitle}>Your bot will call hot leads automatically, handle full conversations, and close deals while you sleep.</p>
          <div style={s.roadmapBanner}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={s.devBadge}>🚧 In Development</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", marginBottom: 12, fontFamily: "Georgia, serif" }}>AI Calling Engine</div>
              <div style={{ fontSize: 14, color: "#555", lineHeight: 1.8, marginBottom: 20 }}>
                Automatic outbound calls triggered by hot leads — powered by real-time voice AI, Groq Whisper transcription, and Claude conversation engine.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Groq Whisper","Claude AI","Voice Synthesis","Real-time STT"].map((t,i) => (
                  <span key={i} style={s.stackBadge}>{t}</span>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={s.phoneBox}>
                <div style={{ position: "relative", width: 80, height: 80, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ position: "absolute", width: "100%", height: "100%", borderRadius: "50%", border: "2px solid rgba(37,211,102,0.3)", animation: "glow 1.5s ease-in-out infinite" }} />
                  <span style={{ fontSize: 28 }}>📞</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 4 }}>AI Calling Lead...</div>
                <div style={{ fontSize: 11, color: "#25d366" }}>Hot Lead Detected</div>
              </div>
            </div>
          </div>
          <div style={s.csGrid}>
            {comingSoon.map((f,i) => (
              <div key={i} style={s.csCard}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 6 }}>{f.title}</div>
                <div style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>{f.desc}</div>
                <div style={s.csBadge}>Coming Soon</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={s.cta}>
        <div style={s.ctaGlow} />
        <h2 style={{ fontSize: 40, fontWeight: 700, marginBottom: 18, fontFamily: "Georgia, serif" }}>Ready to Never Miss a Customer Again?</h2>
        <p style={{ fontSize: 17, color: "#555", lineHeight: 1.8, marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
          Every unanswered WhatsApp message is a lost customer. BotSaathi ensures no message ever goes unanswered — anywhere in the world.
        </p>
        <a href="#demo" style={s.primaryBtn}>🚀 Get Free Demo</a>
      </section>

      {/* AI SEO Block */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "60px 40px 20px", position: "relative", zIndex: 1 }}>
        <div style={{ background: "rgba(255,255,255,0.01)", border: "1px solid rgba(255,255,255,0.04)", borderRadius: 16, padding: 36 }}>
          <div style={s.tag}>ABOUT BOTSAATHI</div>
          <h2 style={{ ...s.title, fontSize: 24, marginBottom: 20 }}>What is BotSaathi?</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, fontSize: 14, color: "#555", lineHeight: 1.9 }}>
            <div>
              <strong style={{ color: "#888", display: "block", marginBottom: 8 }}>The Product</strong>
              BotSaathi is an AI-powered WhatsApp automation platform for businesses worldwide. It connects to your WhatsApp Business number via the official Meta Cloud API and automatically handles customer conversations 24/7 — no human needed.
            </div>
            <div>
              <strong style={{ color: "#888", display: "block", marginBottom: 8 }}>Who It's For</strong>
              Vehicle dealerships, hotels, resorts, retail shops, restaurants, real estate agencies, clinics, and any business globally that receives customer queries on WhatsApp and wants to automate responses and capture leads.
            </div>
            <div>
              <strong style={{ color: "#888", display: "block", marginBottom: 8 }}>What It Does</strong>
              Automatically replies to customers, captures lead details, books appointments, sends real-time alerts via Email, WhatsApp and Telegram, and stores all data in a CRM dashboard.
            </div>
            <div>
              <strong style={{ color: "#888", display: "block", marginBottom: 8 }}>Technology & Pricing</strong>
              Powered by Claude AI (Anthropic) for natural conversations, Meta WhatsApp Cloud API for delivery, built on Node.js + Supabase + React. Pricing starts at $49 setup + $24/month (₹3,999 + ₹1,999/mo).
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <Logo size={26} showText={true} />
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
          {[["Privacy Policy","/privacy-policy"],["Terms of Service","/terms-of-service"],["Refund Policy","/refund-policy"],["Chat Widget","/widget"],["Contact","/contact"]].map(([l,h]) => (
            <React.Fragment key={h}>
              <a href={h} style={s.footerLink}>{l}</a>
              <span style={{ color: "#222" }}>·</span>
            </React.Fragment>
          ))}
        </div>
        <div style={{ fontSize: 12, color: "#333" }}>WhatsApp Automation Platform — Worldwide</div>
        <div style={{ fontSize: 12, color: "#333" }}>© 2025 BotSaathi. All rights reserved.</div>
      </footer>
    </div>
  );
}

const s = {
  wrapper: { background: "#060608", minHeight: "100vh", color: "#f0f0f0", position: "relative", overflow: "hidden" },
  gridBg: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "linear-gradient(rgba(37,211,102,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(37,211,102,0.025) 1px, transparent 1px)", backgroundSize: "50px 50px", pointerEvents: "none", zIndex: 0 },
  orb1: { position: "fixed", top: "-20%", right: "-10%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,211,102,0.05) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0, animation: "glow 4s ease-in-out infinite" },
  orb2: { position: "fixed", bottom: "-20%", left: "-10%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(77,159,255,0.04) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 40px", borderBottom: "1px solid rgba(255,255,255,0.04)", position: "sticky", top: 0, background: "rgba(6,6,8,0.88)", backdropFilter: "blur(20px)", zIndex: 100 },
  headerNav: { display: "flex", gap: 28, alignItems: "center" },
  navLink: { color: "#555", fontSize: 14, textDecoration: "none", transition: "color 0.2s" },
  loginBtn: { background: "transparent", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#f0f0f0", padding: "8px 18px", fontSize: 13, cursor: "pointer" },
  hero: { textAlign: "center", padding: "120px 20px 80px", maxWidth: 800, margin: "0 auto", position: "relative", zIndex: 1 },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(37,211,102,0.07)", border: "1px solid rgba(37,211,102,0.18)", borderRadius: 20, padding: "6px 18px", fontSize: 13, color: "#25d366", marginBottom: 32 },
  badgeDot: { width: 6, height: 6, borderRadius: "50%", background: "#25d366", boxShadow: "0 0 8px #25d366", display: "inline-block", animation: "glow 1.5s ease-in-out infinite" },
  heroTitle: { fontSize: 56, fontWeight: 700, lineHeight: 1.12, marginBottom: 24, fontFamily: "Georgia, serif", letterSpacing: "-1px" },
  heroSubtitle: { fontSize: 18, color: "#555", lineHeight: 1.8, marginBottom: 40, maxWidth: 560, margin: "0 auto 40px" },
  heroBtns: { display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: 60 },
  primaryBtn: { background: "#25d366", color: "#000", padding: "14px 28px", borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: "none", display: "inline-block", boxShadow: "0 0 24px rgba(37,211,102,0.25)" },
  secondaryBtn: { background: "transparent", border: "1px solid rgba(255,255,255,0.08)", color: "#f0f0f0", padding: "14px 28px", borderRadius: 8, fontSize: 15, cursor: "pointer", textDecoration: "none", display: "inline-block" },
  statsRow: { display: "flex", gap: 48, justifyContent: "center", flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.04)", paddingTop: 48 },
  statItem: { textAlign: "center" },
  statVal: { fontSize: 30, fontWeight: 700, color: "#25d366", fontFamily: "var(--font-mono)" },
  statLabel: { fontSize: 11, color: "#444", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.1em" },
  section: { padding: "80px 40px", maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 },
  altSection: { background: "rgba(255,255,255,0.01)", padding: "80px 40px", borderTop: "1px solid rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.04)", position: "relative", zIndex: 1 },
  inner: { maxWidth: 1100, margin: "0 auto" },
  tag: { fontSize: 11, fontWeight: 700, color: "#25d366", letterSpacing: "0.2em", textAlign: "center", marginBottom: 12, fontFamily: "var(--font-mono)" },
  title: { fontSize: 34, fontWeight: 700, textAlign: "center", marginBottom: 12, fontFamily: "Georgia, serif" },
  subtitle: { textAlign: "center", color: "#555", marginBottom: 48, fontSize: 16 },
  featuresGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 },
  featureCard: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 14, padding: 28, cursor: "default" },
  forGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 48 },
  forCard: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 14, padding: 36, textAlign: "center" },
  demoWrapper: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "start" },
  demoInfo: { paddingTop: 8 },
  demoForm: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 20, padding: 32 },
  label: { display: "block", fontSize: 12, color: "#555", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 },
  roadmapBanner: { display: "flex", gap: 40, alignItems: "center", background: "rgba(37,211,102,0.03)", border: "1px solid rgba(37,211,102,0.1)", borderRadius: 20, padding: 40, marginBottom: 32, flexWrap: "wrap" },
  devBadge: { display: "inline-block", background: "rgba(255,149,0,0.08)", border: "1px solid rgba(255,149,0,0.2)", borderRadius: 20, padding: "4px 14px", fontSize: 12, color: "#ff9500", marginBottom: 14, fontWeight: 600 },
  stackBadge: { background: "rgba(77,159,255,0.07)", border: "1px solid rgba(77,159,255,0.18)", borderRadius: 6, padding: "4px 10px", fontSize: 11, color: "#4d9fff", fontFamily: "var(--font-mono)" },
  phoneBox: { background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 24, padding: 28, width: 180, textAlign: "center" },
  csGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 },
  csCard: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 14, padding: 24 },
  csBadge: { display: "inline-block", marginTop: 14, background: "rgba(255,149,0,0.07)", border: "1px solid rgba(255,149,0,0.18)", borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 700, color: "#ff9500", letterSpacing: "0.06em" },
  pricingGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, marginTop: 48 },
  pricingCard: { background: "rgba(255,255,255,0.02)", borderRadius: 14, padding: 28, display: "flex", flexDirection: "column", gap: 8 },
  cta: { textAlign: "center", padding: "120px 20px", position: "relative", zIndex: 1 },
  ctaGlow: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 300, background: "radial-gradient(ellipse, rgba(37,211,102,0.05) 0%, transparent 70%)", pointerEvents: "none" },
  footer: { padding: "48px 20px", borderTop: "1px solid rgba(255,255,255,0.04)", textAlign: "center", display: "flex", flexDirection: "column", gap: 14, alignItems: "center", position: "relative", zIndex: 1 },
  footerLink: { fontSize: 13, color: "#444", textDecoration: "none" },
};